import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { VariantProduct } from '__generated__/graphql';
import { useCart } from 'hooks/useCart';
import {
  ActiveCart,
  ActiveCartGiftWithPurchases,
  ActiveCartItem,
} from 'types/cart';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { useTranslate } from 'hooks/useTranslations';

import { SelectedItemType, ItemIdentifiersType } from './types';
import {
  doesSelectedItemMatchInCartItem,
  findSizeObject,
  handleAddItemsToCart,
  handleRemoveItemFromCart,
  handleUpdateQuantity,
  handleUpdatingPromoGifts,
  selectedItemsInCart,
  isMatchForSelectedItem,
  removeSelectedItem,
  addSelectedItem,
} from './utils';

export const useGWPModal = ({
  giftWithPurchase,
  cart,
  closeModal,
  setLoading,
  setError,
}: {
  giftWithPurchase?: ActiveCartGiftWithPurchases;
  cart?: ActiveCart;
  closeModal: () => void;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<string | undefined>>;
}) => {
  const t = useTranslate();
  const { currency } = useSiteConfig();
  const { addToCart, updateInCart, removeFromCart } = useCart();

  const doesSelectedItemMatchInCartItemCallback = useCallback(
    (selected: SelectedItemType, inCart: ActiveCartItem) =>
      doesSelectedItemMatchInCartItem(selected, inCart, giftWithPurchase),
    [giftWithPurchase]
  );

  const findSizeObjectCallback = useCallback(
    (item: VariantProduct) => findSizeObject(item, giftWithPurchase),
    [giftWithPurchase]
  );

  const selectedItemsInCartMemo: SelectedItemType[] = useMemo(
    () => selectedItemsInCart(cart, findSizeObjectCallback, giftWithPurchase),
    [cart, giftWithPurchase, findSizeObjectCallback]
  );

  const [selectedItems, setSelectedItems] = useState<SelectedItemType[]>(
    selectedItemsInCartMemo
  );

  const handleUpdateQuantityCallback = useCallback(
    async (itemsToUpdate: SelectedItemType[]) =>
      handleUpdateQuantity(
        itemsToUpdate,
        cart,
        doesSelectedItemMatchInCartItemCallback,
        updateInCart,
        currency.code
      ),
    [cart, doesSelectedItemMatchInCartItemCallback, updateInCart, currency.code]
  );

  const handleAddItemsToCartCallback = useCallback(
    async (itemsToAdd: SelectedItemType[], showMiniCart?: boolean) =>
      handleAddItemsToCart(
        itemsToAdd,
        addToCart,
        giftWithPurchase,
        showMiniCart
      ),
    [addToCart, giftWithPurchase]
  );

  // Removes items one at a time to keep cart in sync
  const handleRemoveItemFromCartCallback = useCallback(
    async (itemsToRemove: SelectedItemType[]) =>
      handleRemoveItemFromCart(
        itemsToRemove,
        cart,
        doesSelectedItemMatchInCartItemCallback,
        removeFromCart
      ),
    [cart, doesSelectedItemMatchInCartItemCallback, removeFromCart]
  );

  // When user clicks "Add to cart" we need to figure out which items need to be
  // removed from cart, updated in quantity or added to cart. We then call them
  // sequentially so we are always operating on the updated cart.

  const handleUpdatingPromoGiftsCallback = useCallback(
    async (props?: { showMiniCart?: boolean }) => {
      handleUpdatingPromoGifts(
        handleAddItemsToCartCallback,
        handleRemoveItemFromCartCallback,
        handleUpdateQuantityCallback,
        closeModal,
        selectedItems,
        selectedItemsInCartMemo,
        setLoading,
        props?.showMiniCart
      );
    },
    [
      handleAddItemsToCartCallback,
      handleRemoveItemFromCartCallback,
      handleUpdateQuantityCallback,
      closeModal,
      selectedItems,
      selectedItemsInCartMemo,
      setLoading,
    ]
  );

  // Remove items from selectedItems array that match id, color, and size
  const removeSelectedItemCallback = useCallback(
    (itemToRemove: ItemIdentifiersType) =>
      removeSelectedItem(itemToRemove, setSelectedItems, selectedItems),
    [setSelectedItems, selectedItems]
  );

  // Add new item to selectedItems array
  const addSelectedItemCallback = useCallback(
    (itemToAdd: SelectedItemType) =>
      addSelectedItem(itemToAdd, setSelectedItems, selectedItems),
    [selectedItems, setSelectedItems]
  );

  // Updates Quantity of Selected Item
  const updateSelectedItemQuantity = useCallback(
    (itemIdentifiers: ItemIdentifiersType, newQuantity: number) => {
      // if there are selected items, update the quantity there
      if (selectedItems.length) {
        // Update matching selectedItem
        const updatedSelectedItems = selectedItems.map(selectedItem => {
          if (isMatchForSelectedItem(itemIdentifiers, selectedItem)) {
            return {
              ...selectedItem,
              quantity: newQuantity,
            };
          } else {
            return selectedItem;
          }
        });
        setSelectedItems(updatedSelectedItems);
      }
    },
    [selectedItems, setSelectedItems]
  );

  // Each time selectedItems changes, update selectedQuantity
  const selectedQuantity = useMemo(() => {
    return selectedItems.reduce((acc, curr) => acc + curr.quantity, 0);
  }, [selectedItems]);

  // Each time selectedQuantity changes, determine if user has selected more
  // than the max allowed. If so, show error. Otherwise, clear error message.
  useEffect(() => {
    if (giftWithPurchase?.allowedQuantity) {
      if (selectedQuantity > giftWithPurchase.allowedQuantity) {
        setError(
          t<'maxBonusItems'>('maxBonusItems', {
            allowedQuantity: giftWithPurchase.allowedQuantity,
          })
        );
      } else setError(undefined);
    }
  }, [giftWithPurchase?.allowedQuantity, selectedQuantity, setError, t]);

  return {
    handleUpdatingPromoGiftsCallback,
    removeSelectedItemCallback,
    addSelectedItemCallback,
    updateSelectedItemQuantity,
    selectedItems,
    selectedQuantity,
  };
};
